import { merge } from 'lodash';
import React, { useMemo, useCallback } from 'react';

import { CategorySortFields, SortDirection, GetCategoriesQueryVariables } from '@/graphql';
import { BaseTableProps } from '@boilerplate/types/entity';
import Entity from '@/entities/category';
import EntityTable, {
  defaultPage,
  defaultPageSize,
  defaultSortOrder,
  getDefaultSortField,
} from '@boilerplate/components/entity/EntityTable/EntityTable';
import { canPerformAction, useAuthenticatedUserStore } from '@/stores/UserStore';
import { openConfirmationDialog } from '@/stores/ConfirmationDialogStore';


export const useCategoriesTable = ({ queryOptions }: { queryOptions?: GetCategoriesQueryVariables } = {}) => {
  const defaultVariableOptions = useMemo<GetCategoriesQueryVariables>(
    () => 
      merge(
        {
          sorting: {
            direction: defaultSortOrder === 'desc' ? SortDirection.Desc : SortDirection.Asc,
            field: getDefaultSortField(Entity.table.schema) as CategorySortFields,
          },
          paging: {
            page: defaultPage,
            pageSize: defaultPageSize,
          },
                  withParentCategory: true,
                  withSubCategories: true,
                  withTenant: true,
          },
          queryOptions,
        ), 
    [queryOptions]
    );
  
  const {
    data: categoryData,
    items = [],
    loading,
    refetch: originalRefetch,
    variables,
  } = Entity.model.useGetAll({
    fetchPolicy: 'cache-and-network',
    variables: defaultVariableOptions,
  });

  const [remove] = Entity.model.useDelete();

  const refetch = useCallback(
    (variableOptions?: Partial<GetCategoriesQueryVariables >) => {
      return originalRefetch(merge({}, defaultVariableOptions, variableOptions));
    },
    [defaultVariableOptions, originalRefetch]
  );

  const allActions = useMemo(() => ({ loading, refetch, remove }), [loading, refetch, remove]);
  const data = useMemo(
    () =>
      categoryData
        ? { totalCount: categoryData.categories.totalCount, rows: items }
        : { totalCount: 0, rows: [] },
    [categoryData, items]
  );

  return { data, actions: allActions, refetch, defaultVariableOptions, variables };
}

export type CategoriesBaseTableProps = BaseTableProps<typeof Entity.model>;

export default function CategoriesBaseTable(props: CategoriesBaseTableProps) {
  const tableProps = useCategoriesTable();
  const authentictedUser = useAuthenticatedUserStore();

  return (
    <EntityTable
      {...props}
      {...tableProps}
      Entity={Entity}
      user={authentictedUser}
      canPerformThisAction={canPerformAction}
      openConfirmationDialog={openConfirmationDialog}
    />
  );
}
